import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client/react';
import MiniWidgetsCard from './mini-widgets-card';
import PersonalInformationCard from './personal-information-card';
import SiteInformationCard from './site-information-card';
import styles from './style.module.scss';
import SubjectStatusCard from './subject-status-card';
import TimelineCard from './timeline-card';
import TreatmentStatusCard from './treatment-status-card';
import EditCard from '@components-clean/core/edit-card';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { useLocalStateFieldsMapped } from '@hooks-clean/index';
import { LocalStateFieldWithKeyAndName } from '@hooks-clean/use-local-state-fields';
import { useBoolean, useTranslation } from '@hooks/index';
import { javascriptDateWithoutTimeFromUTC, stringDateWithoutTimeFromLocal } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
// @ts-expect-error @types collides with node_modules/@types
import { FieldDefinitionFieldType, PanelType, RowDataSourceTable } from '@types/graphql-global-types';
import {
    GET_SM_PANELS,
    GET_SUBJECT_PROFILE_DETAILS_CONFIGURATION,
    UPSERT_SUBJECT_PROFILE_DETAILS_CONFIGURATION,
} from '@queries/subject-monitoring';
import { LOVField, PanelListItem, SubjectProfileDetailsConfigurationFormValues } from './types';

interface Props {
    studyRef: string;
    organisationId: string;
    canEdit: boolean;
}

const COMPONENT_NAME = 'SUBJECT_PROFILE_DETAILS_CONFIGURATION';

const dateFieldsFieldTypes = [FieldDefinitionFieldType.DATE, FieldDefinitionFieldType.PARTIAL_DATE];
const dateFieldsDataTypes = [RowDataSourceTable.SUBJECT];
// Maps field to just key-name pair
const mapFieldToLOV = (field: LocalStateFieldWithKeyAndName) => ({ key: field.key, name: field.name });

const SubjectProfileDetailsConfig = ({ studyRef, organisationId, canEdit }: Props) => {
    const { confirmDiscardChanges, anyComponentHasChanges, setComponentHasChanges } = useUnsavedChangesContext();
    const { t } = useTranslation('admin');

    const sortLOV = useMemo<LOVField[]>(() => {
        return [
            { key: 'asc', name: t('subjectConfig.subjectProfileDetailsConfig.timeline.sortLOV.asc') },
            { key: 'desc', name: t('subjectConfig.subjectProfileDetailsConfig.timeline.sortLOV.desc') },
        ];
    }, [t]);

    const dateFieldsLOV = useLocalStateFieldsMapped<LOVField>({
        isCalculated: false,
        isHidden: false,
        fieldTypes: dateFieldsFieldTypes,
        dataTypes: dateFieldsDataTypes,
        mapFn: mapFieldToLOV,
    });

    const isEditMode = useBoolean(false);

    const smPanelsQuery = useQuery(GET_SM_PANELS, { variables: { studyId: studyRef, organisationId } });

    const timelinePanelsListItems = useMemo<PanelListItem[]>(() => {
        return (
            (smPanelsQuery.data?.smPanels ?? [])
                // Only timeline panels
                .filter((x) => x != null)
                .filter((x) => x.panelType === PanelType.TIMELINE)
                // Checked needed fields are defined and expected type (everything is `any`...)
                .filter(
                    (x) =>
                        typeof x.id === 'string' &&
                        typeof x.recordId === 'number' &&
                        typeof x.plotTitle === 'string' &&
                        typeof x.active === 'boolean',
                )
                // Extract out necessary fields
                .map((x) => ({
                    id: String(x.id),
                    plotTitle: String(x.plotTitle),
                    title: `${x.recordId}: ${x.plotTitle}`,
                    active: Boolean(x.active),
                }))
        );
    }, [smPanelsQuery.data]);

    const subjectProfileDetailsConfigQuery = useQuery(GET_SUBJECT_PROFILE_DETAILS_CONFIGURATION, {
        variables: { studyId: studyRef },
    });

    const [upsertConfig] = useMutation(UPSERT_SUBJECT_PROFILE_DETAILS_CONFIGURATION);

    const defaultValues = useMemo<SubjectProfileDetailsConfigurationFormValues>(() => {
        const item =
            subjectProfileDetailsConfigQuery.data?.subjectProfileDetailsConfiguration
                ?.subjectProfileDetailsConfigurationItem;
        const personalInformation = item?.personalInformation;
        const subjectStatus = item?.subjectStatus;
        const treatmentStatus = item?.treatmentStatus;
        const siteInformation = item?.siteInformation;
        const timeline = item?.timeline;

        // Map timeline values to form values...
        const startDate = timeline?.startDate != null ? javascriptDateWithoutTimeFromUTC(timeline.startDate) : null;

        const dateFieldLOVMap = new Map(dateFieldsLOV.map(({ key, name }) => [key, name]));
        const endDateFields = (timeline?.endDateFields ?? []).map((key) => ({
            key,
            name: dateFieldLOVMap.get(key) ?? key, // Default to key if unable to find matching LOV name
        }));

        const milestoneDateFields = (timeline?.milestoneDateFields ?? []).map((key) => ({
            key,
            name: dateFieldLOVMap.get(key) ?? key, // Default to key if unable to find matching LOV name
        }));

        const sortKey = timeline?.sortAscending === false ? 'desc' : 'asc'; // Default to 'asc' if undefined
        const sort = sortLOV.find((f) => f.key === sortKey)!;

        const timelinePanelsById = new Map(timelinePanelsListItems.map((x) => [x.id, x]));
        const panels = (timeline?.panels ?? [])
            // Map panel id to PanelListItem. Remove any that fail to map
            .map((x) => timelinePanelsById.get(x.id))
            .filter((x) => x != null);

        return {
            personalInformation: {
                label: personalInformation?.label ?? 'Subject Information',
                ageAtEnrolment: personalInformation?.ageAtEnrolment ?? true,
                gender: personalInformation?.gender ?? true,
                race: personalInformation?.race ?? true,
                ethnicity: personalInformation?.ethnicity ?? true,
                latestConsentDate: personalInformation?.latestConsentDate ?? true,
                latestConsentVersion: personalInformation?.latestConsentVersion ?? true,
            },
            subjectStatus: {
                label: subjectStatus?.label ?? 'Subject Status',
                status: subjectStatus?.status ?? true,
                screening: subjectStatus?.screening ?? true,
                screenFail: subjectStatus?.screenFail ?? true,
                screenFailReason: subjectStatus?.screenFailReason ?? true,
                enrolled: subjectStatus?.enrolled ?? true,
                randomized: subjectStatus?.randomized ?? true,
                fUStart: subjectStatus?.fUStart ?? true,
                fUCompleted: subjectStatus?.fUCompleted ?? true,
                studyDisc: subjectStatus?.studyDisc ?? true,
                studyDiscReason: subjectStatus?.studyDiscReason ?? true,
                lastVisitNameAndDate: subjectStatus?.lastVisitNameAndDate ?? true,

                numberOfAEs: subjectStatus?.numberOfAEs ?? true,
                numberOfSAEs: subjectStatus?.numberOfSAEs ?? true,
                numberOfAESIs: subjectStatus?.numberOfAESIs ?? true,
                numberOfPDs: subjectStatus?.numberOfPDs ?? true,
                numberOfQueriesOpenAll: subjectStatus?.numberOfQueriesOpenAll ?? true,
                combiVisits: subjectStatus?.combiVisits ?? true,
                combiAssessments: subjectStatus?.combiAssessments ?? true,
            },
            treatmentStatus: {
                label: treatmentStatus?.label ?? 'Treatment Status',
                cohort: treatmentStatus?.cohort ?? true,
                treatmentArm: treatmentStatus?.treatmentArm ?? true,
                txStart: treatmentStatus?.txStart ?? true,
                txDisc: treatmentStatus?.txDisc ?? true,
                txDiscReason: treatmentStatus?.txDiscReason ?? true,
                txCompleted: treatmentStatus?.txCompleted ?? true,
            },
            siteInformation: {
                label: siteInformation?.label ?? 'Site Information',
                status: siteInformation?.status ?? true,
                country: siteInformation?.country ?? true,
                activeDate: siteInformation?.activeDate ?? true,
                closedDate: siteInformation?.closedDate ?? true,
                monitor: siteInformation?.monitor ?? true,
                investigatorName: siteInformation?.investigatorName ?? true,
                subjectsScreened: siteInformation?.subjectsScreened ?? true,
                subjectsScreenFailed: siteInformation?.subjectsScreenFailed ?? true,
                subjectEnrolled: siteInformation?.subjectEnrolled ?? true,
                subjectRandomized: siteInformation?.subjectRandomized ?? true,
                subjectTxStart: siteInformation?.subjectTxStart ?? true,
                subjectTxDisc: siteInformation?.subjectTxDisc ?? true,
                subjectsTxComplete: siteInformation?.subjectsTxComplete ?? true,
                subjectsFUStart: siteInformation?.subjectsFUStart ?? true,
                subjectsFUComplete: siteInformation?.subjectsFUComplete ?? true,
                numberOfAEs: siteInformation?.numberOfAEs ?? true,
                numberOfSAEs: siteInformation?.numberOfSAEs ?? true,
                numberOfAESIs: siteInformation?.numberOfAESIs ?? true,
                numberOfPDs: siteInformation?.numberOfPDs ?? true,
                numberOfQueriesOpenAll: siteInformation?.numberOfQueriesOpenAll ?? true,
            },
            timeline: {
                startDate,
                endDateFields,
                sort,
                milestoneDateFields,
                showTagsAsMilestones: timeline?.showTagsAsMilestones ?? false,
                panels,
            },
        };
    }, [subjectProfileDetailsConfigQuery.data, timelinePanelsListItems, dateFieldsLOV, sortLOV]);

    const {
        control,
        reset,
        handleSubmit,
        formState: { isDirty, isValid, isSubmitting },
    } = useForm<SubjectProfileDetailsConfigurationFormValues>({
        mode: 'all',
        defaultValues,
    });

    useEffect(() => {
        reset(defaultValues, { keepDirty: false });
    }, [reset, defaultValues]);

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, [setComponentHasChanges, isDirty]);

    function exitEditMode() {
        isEditMode.setFalse();
        setComponentHasChanges(COMPONENT_NAME, false);
    }

    const onSubmit = handleSubmit(async (saveData) => {
        if (!isDirty) {
            exitEditMode();
            return;
        }

        const { timeline, ...rest } = saveData;

        try {
            await upsertConfig({
                variables: {
                    studyId: studyRef,
                    configItem: {
                        ...rest,
                        timeline: {
                            startDate: stringDateWithoutTimeFromLocal(timeline.startDate!),
                            endDateFields: timeline.endDateFields.map((x) => x.key),
                            sortAscending: timeline.sort.key === 'asc',
                            milestoneDateFields: timeline.milestoneDateFields.map((x) => x.key),
                            showTagsAsMilestones: timeline.showTagsAsMilestones,
                            panels: timeline.panels.map(({ id, plotTitle }) => ({
                                id,
                                plotTitle,
                            })),
                        },
                    },
                },
            });
            exitEditMode();
            ShowSuccess(t('common:actionsSuccessfulToast'));
            await subjectProfileDetailsConfigQuery.refetch({ studyId: studyRef });
        } catch {
            ShowError(t('common:errorMessage'));
        }
    });

    const onEditCardCancel = async () => {
        if (anyComponentHasChanges()) {
            const confirmed = await confirmDiscardChanges();
            if (!confirmed) {
                return;
            }
            reset(defaultValues, { keepDirty: false });
        }
        exitEditMode();
    };

    const isLoading = smPanelsQuery.loading || subjectProfileDetailsConfigQuery.loading;
    // On fetch, if no config exists, then a default config should be created, saved, and returned for the study
    // So data should never finish loading and be null
    const isError =
        smPanelsQuery.error != null ||
        subjectProfileDetailsConfigQuery.error != null ||
        (!subjectProfileDetailsConfigQuery.loading && subjectProfileDetailsConfigQuery.data == null);

    const canSubmit = isDirty && isValid && !isSubmitting;

    return (
        <EditCard
            title={t('subjectConfig.subjectProfileDetailsConfig.title')}
            onEdit={isEditMode.setTrue}
            isEditMode={isEditMode.value}
            onCancel={onEditCardCancel}
            onSave={onSubmit}
            disabled={!canEdit}
            saveDisabled={!canSubmit}
            className={styles.editCard}
            data-testid="subject-profie-details-configuration"
        >
            {isLoading ? (
                <Loading />
            ) : isError ? (
                <InfoField type="Error" message="Something went wrong!" />
            ) : (
                <div className={styles.editCardBody}>
                    <TimelineCard
                        control={control}
                        defaultValues={defaultValues}
                        disabled={!isEditMode.value}
                        dateFieldsLOV={dateFieldsLOV}
                        sortLOV={sortLOV}
                        timelinePanelsListItems={timelinePanelsListItems}
                    />

                    <PersonalInformationCard control={control} disabled={!isEditMode.value} />

                    <SubjectStatusCard control={control} disabled={!isEditMode.value} />

                    <TreatmentStatusCard control={control} disabled={!isEditMode.value} />

                    <SiteInformationCard control={control} disabled={!isEditMode.value} />

                    <MiniWidgetsCard control={control} disabled={!isEditMode.value} />
                </div>
            )}
        </EditCard>
    );
};

export default SubjectProfileDetailsConfig;
